/**
 * PRODUCT CATALOGUE & ON-SITE SALES — organizer API
 *
 * The catalogue is organizer-level and reusable: a product is entered once and can be either
 * sold standalone via a printed QR code, or selected into an event as an add-on. The two
 * channels are independent — see ONSITE_PRODUCTS_DESIGN.md.
 *
 * axiosInstance attaches the x-access-token header and handles auth expiry.
 */
import axiosInstance from "./axiosInstance";
import { getUrlForBookings } from "./confighelpers";

const unwrap = (response) => {
  if (response?.status === 200 && response?.data?.statusCode === 200) {
    return { error: false, data: response.data.data };
  }
  return { error: true, message: response?.data?.message || "Something went wrong" };
};

/**
 * The URL encoded into a printed QR code.
 *
 * Read from `product.onsiteSaleUrl`, which the backend derives from BOOKING_LINK — the same
 * env var behind event booking links and manage-booking links. We deliberately do NOT compose
 * it here: a second source would let QR codes drift away from the rest of the platform.
 */
export const getOnsiteSaleUrl = (product) => product?.onsiteSaleUrl || "";

export async function listProducts({ includeInactive = false } = {}) {
  try {
    const response = await axiosInstance({
      method: "get",
      url: `${getUrlForBookings()}products/list${includeInactive ? "?includeInactive=true" : ""}`,
    });
    return unwrap(response);
  } catch (e) {
    return { error: true, message: e?.message || "Could not load products" };
  }
}

/**
 * The organizer's public web-shop address — one URL covering the whole catalogue, as
 * opposed to the per-product sticker codes.
 *
 * The slug is created server-side on first call, so simply opening My Products is what
 * brings a shop into existence. Returns { slug, url, sellableCount }.
 */
export async function getMyWebShop() {
  try {
    const response = await axiosInstance({
      method: "get",
      url: `${getUrlForBookings()}products/webshop`,
    });
    return unwrap(response);
  } catch (e) {
    return { error: true, message: e?.message || "Could not load your web shop" };
  }
}

export async function addProduct(payload) {
  try {
    const response = await axiosInstance({
      method: "post",
      url: `${getUrlForBookings()}products/add`,
      data: payload,
    });
    return unwrap(response);
  } catch (e) {
    return { error: true, message: e?.message || "Could not create product" };
  }
}

export async function updateProduct(payload) {
  try {
    const response = await axiosInstance({
      method: "post",
      url: `${getUrlForBookings()}products/update`,
      data: payload,
    });
    return unwrap(response);
  } catch (e) {
    return { error: true, message: e?.message || "Could not update product" };
  }
}

export async function archiveProduct(_id) {
  try {
    const response = await axiosInstance({
      method: "post",
      url: `${getUrlForBookings()}products/archive`,
      data: { _id },
    });
    return unwrap(response);
  } catch (e) {
    return { error: true, message: e?.message || "Could not archive product" };
  }
}

/** On-site sales already mirrored locally. */
export async function listOnsiteSales({ productId, vendorId, from, to } = {}) {
  try {
    const params = new URLSearchParams();
    if (productId) params.set("productId", productId);
    if (vendorId) params.set("vendorId", vendorId);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString();

    const response = await axiosInstance({
      method: "get",
      url: `${getUrlForBookings()}products/onsite/sales${qs ? `?${qs}` : ""}`,
    });
    return unwrap(response);
  } catch (e) {
    return { error: true, message: e?.message || "Could not load sales" };
  }
}

/**
 * Send Stripe's receipt for a sale to a given address.
 * Stripe has no resend API — it only sends when the address changes — so the caller must
 * supply an address different from the one already on the charge.
 */
export async function sendOnsiteReceipt({ saleId, email }) {
  try {
    const response = await axiosInstance({
      method: "post",
      url: `${getUrlForBookings()}products/onsite/send-receipt`,
      data: { saleId, email },
    });
    return unwrap(response);
  } catch (e) {
    const message = e?.response?.data?.message;
    return { error: true, message: message || e?.message || "Could not send receipt" };
  }
}

/**
 * Pull payments from Stripe and refresh local rows.
 * Slow by nature (paginated Stripe search), so the caller should show progress and the
 * "may take a couple of minutes" note.
 */
export async function syncOnsiteSales({ days = 30 } = {}) {
  try {
    const response = await axiosInstance({
      method: "post",
      url: `${getUrlForBookings()}products/onsite/sync`,
      data: { days },
      timeout: 120000, // well beyond the default 10s — this paginates through Stripe
    });
    return unwrap(response);
  } catch (e) {
    return { error: true, message: e?.message || "Sync failed" };
  }
}
